import axios from 'axios'
import exceptionHandler from '@/common/exception-handler'
import _ from 'lodash'
import locationSettingsDefaults from './location-settings-defaults'

const setLastUserLocationSettingsLocation = (email, locationId) => {
  localStorage[`user_location_settings_location::${email}`] = locationId
}

const getLastUserLocationSettingsLocation = email => {
  return localStorage[`user_location_settings_location::${email}`]
}

const getCompanySettings = (companyId, expectedVersion, expectedId) => {
  return getSettings(`/api/blobs/settings/company/${companyId}`, expectedVersion, expectedId)
}

const getAllSettingsOverrides = (companyId) => {
  return getSettings(`/api/blobs/settings/overrides/${companyId}`)
}

const getTemplateSettings = () => {
  return getSettings('/api/blobs/settings/template')
}

const getPrintingTemplateSettingsByBlobId = (companyId, blobId) => {
  return getSettings(`/api/blobs/settings/printing-template/${companyId}/${blobId}`)
}

const saveLocationSettings = (locationId, overrides, loadedLocationId, attempt) => {
  // TPOS-10162: loadedLocationId is the locationId the caller loaded settings for, captured
  // independently at load time — sent as a query param so the back-end can reject the save if
  // it disagrees with the URL's locationId (defense in depth against a stale/cached session).
  return axios.put(`/api/blobs/settings/overrides/location/${locationId}`, overrides, { params: { loadedLocationId } })
    .catch(error => exceptionHandler.parseWithRetryWhenConflict(error, attempt,
      next => saveLocationSettings(locationId, overrides, loadedLocationId, next)))
}

const saveCompanySettings = (companyId, settingsBlob) => {
  return axios.put(`/api/blobs/settings/company/${companyId}`, settingsBlob)
    .catch(error => exceptionHandler.parse(error))
}

/**
 * Request back-end to build the mobile app.
 *
 * @param {String} accountId - should be the tcc company id.
 * @param {Object} requestData - { adHocProvisioningProfile, apnsKey, appStoreProvisioningProfile, icon, iosCertificate,
 *    name, keyPassword, keystore, keystoreAlias, keystorePassword, packageName, splashImage, version }
 * @returns promise
 */
const buildMobileApp = (accountId, requestData) => {
  return axios.post('/api/buildMobileApp?accountId=' + accountId, requestData)
}

const fetchMobileAppUrls = (accountId) => {
  return new Promise(resolve => {
    axios.get('/api/tma/', {
      params: {
        accountId
      }
    }).then(response => {
      resolve(response ? response.data : response)
    }).catch(() => {
      resolve(false)
    })
  })
}

export default {
  setLastUserLocationSettingsLocation,
  getLastUserLocationSettingsLocation,
  getCompanySettings,
  getTemplateSettings,
  getAllSettingsOverrides,
  getPrintingTemplateSettingsByBlobId,
  getLocationSettings (locationId, expected) {
    return axios.get(`/api/blobs/settings/location/${locationId}`, { params: expected || null })
      .then(res => {
        let data = res.data

        if (!data.locationBlob && !data.override) {
          return 304
        }

        let diff = null
        let groupDiff = null
        if (!data.override) {
          if (!expected || !expected.expectedOverrideVer) {
            diff = { changes: [] }
          }
        } else {
          diff = JSON.parse(data.override)
        }

        if (data.groupOverride) {
          groupDiff = JSON.parse(data.groupOverride)
        }

        delete data.override
        delete data.groupOverride

        if (data.locationBlob) {
          data.settingsBlob = JSON.parse(data.locationBlob)
        }

        return { base: data, diff, groupDiff }
      })
      .catch(error => exceptionHandler.parse(error))
  },
  saveLocationSettings,
  saveCompanySettings,
  buildMobileApp,
  fetchMobileAppUrls,
  getPublishedSettingsBlob (locationId) {
    return axios.get('/api/published-config/location-settings/' + locationId)
      .then(result => result.data)
  }

}

function getSettings (url, expectedVersion, expectedId) {
  return axios.get(url, { params: { ...(expectedVersion ? { expectedVersion, expectedId } : {}) } })
    .then(res => {
      let locationSettings = res.data

      if (locationSettings.locationBlob) {
        locationSettings.settingsBlob = JSON.parse(locationSettings.locationBlob)
      } else {
        locationSettings.settingsBlob = locationSettingsDefaults()
        locationSettings.$default = true
      }

      return locationSettings
    })
    .catch(error => {
      if (_.get(error, 'response.status') === 304) {
        return 304
      }

      return exceptionHandler.parse(error)
    })
}
